/**
 * AttributeScreen1.tsx - First screen of the Attribute Framing experiment
 * Last updated: 2025-11-11
 * Updated by: JulianPolly, Copilot
 */

import React, { useEffect, useState } from "react";
import { Icon } from "@blueprintjs/core";
import recyclingBecherImg from "./assets/Recyclingbecher.png";
import neuplastikBecherImg from "./assets/Neuplastikbecher.png";
import { ScreenProps } from "../../SequenceController";
import { ScreenConfig } from "../Sequences";
import { useLanguage } from "../../../../context/LanguageContext";
import { colors, commonStyles } from "../../../../components/colors";

// Define choice type for better type safety
export type AttributeChoice = 0 | 1; // 0 = recycled, 1 = new plastic

export interface SelectionData {
    choice: number;
    millisecondsSinceFirstRender: number;
}

export interface AttributeScreenResult {
    selections: SelectionData[];
    usedHelp: boolean;
    reason?: string; // Add reason to result
}

export const AttributeScreenInitialResult: AttributeScreenResult = {
    selections: [],
    usedHelp: false,
    reason: "" // Initialize empty
};

function CupOptions({ selected, setSelected }: {
    selected: number | null,
    setSelected: (choice: number) => void
}) {
    const { t } = useLanguage();

    const options = [
        {
            value: 0,
            title: t('experiments', 'attributeFraming', 'screens', 'task', 'options', 'recycled', 'title'),
            description: t('experiments', 'attributeFraming', 'screens', 'task', 'options', 'recycled', 'description'),
            image: recyclingBecherImg
        },
        {
            value: 1,
            title: t('experiments', 'attributeFraming', 'screens', 'task', 'options', 'new', 'title'),
            description: t('experiments', 'attributeFraming', 'screens', 'task', 'options', 'new', 'description'),
            image: neuplastikBecherImg
        }
    ];

    return (
        <div style={{
            width: '100%',
            display: "flex",
            flexDirection: "column",
            gap: 24
        }}>
            <div style={{
                display: "flex",
                gap: 24,
                flexDirection: window.innerWidth < 600 ? "column" : "row"
            }}>
                {options.map((option) => (
                    <div
                        key={option.value}
                        style={selected === option.value
                            ? commonStyles.optionCard.selected
                            : commonStyles.optionCard.default}
                        onClick={() => setSelected(option.value)}
                    >
                        {selected === option.value && (
                            <div style={commonStyles.selectionCheck}>
                                <Icon icon="tick" size={16} />
                            </div>
                        )}

                        {/* Cup image */}
                        <div style={{
                            marginBottom: 20,
                            borderRadius: 12,
                            overflow: "hidden",
                            boxShadow: "0 6px 20px 0 rgba(0,0,0,0.09)"
                        }}>
                            <img
                                src={option.image}
                                alt={option.title}
                                style={{
                                    width: "100%",
                                    height: "auto",
                                    maxHeight: 240,
                                    objectFit: "contain",
                                    display: "block",
                                    padding: "20px 10px",
                                    background: "#f8f9fa"
                                }}
                            />
                        </div>

                        <h3 style={{
                            color: colors.brillantBlue,
                            marginBottom: 16,
                            fontSize: 20,
                            fontWeight: 600
                        }}>
                            {option.title}
                        </h3>

                        <p style={{
                            fontSize: 17,
                            lineHeight: 1.6,
                            fontWeight: 500
                        }}>
                            {option.description}
                        </p>
                    </div>
                ))}
            </div>
        </div>
    );
}

export function AttributeScreen1(props: ScreenProps) {
    const { t, language } = useLanguage();
    const [firstRenderTimestamp, setFirstRenderTimestamp] = useState(0);
    const [selected, setSelected] = useState<number | null>(null);

    // Reason logic: NEW
    const [reason, setReason] = useState<string>("");
    const [reasonTouched, setReasonTouched] = useState(false);
    const [reasonError, setReasonError] = useState<string>("");

    const { setCanContinue, setResult } = props;

    // First render: reset all
    useEffect(() => {
        setFirstRenderTimestamp(Date.now());
        setCanContinue(false);
        setResult(() => ({ ...AttributeScreenInitialResult }));
        setReason("");
        setReasonTouched(false);
        setReasonError("");
    }, [setCanContinue, setResult]);

    // Validation logic: both selected and reason required
    useEffect(() => {
        const reasonOk = reason.trim().length > 0;
        if (!reasonOk && reasonTouched) {
            setReasonError(
                t('experiments', 'attributeFraming', 'screens', 'task', 'reasonRequired') ||
                (language === "de" ? "(erforderlich)" : "(required)")
            );
        } else {
            setReasonError("");
        }

        const canContinue = selected !== null && reasonOk;
        setCanContinue(canContinue);

        if (canContinue && selected !== null) {
            setResult((prev: AttributeScreenResult) => ({
                ...(prev || {}),
                selections: [{
                    choice: selected,
                    millisecondsSinceFirstRender: Date.now() - firstRenderTimestamp
                }],
                usedHelp: prev?.usedHelp || false,
                reason: reason.trim()
            }));
        } else {
            // Only update selection if needed, but keep incomplete result
            setResult((prev: AttributeScreenResult) => ({
                ...prev,
                selections: selected !== null
                    ? [{
                        choice: selected,
                        millisecondsSinceFirstRender: Date.now() - firstRenderTimestamp
                    }]
                    : [],
                reason: reason
            }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selected, reason, reasonTouched, setCanContinue, setResult, firstRenderTimestamp, t, language]);

    return (
        <div className="experiment-container" style={{
            maxWidth: 1250,
            margin: "0 auto",
            padding: "24px 20px 44px",
            backgroundColor: colors.backgroundLight
        }}>
            {/* Content card - contains header and instructions */}
            <div style={{
                background: colors.white,
                borderRadius: 14,
                boxShadow: "0 2px 12px rgba(0,0,0,0.09)",
                padding: "24px 40px 28px",
                marginBottom: 28
            }}>
                {/* Heading moved inside the card */}
                <h2 style={{
                    color: colors.brillantBlue,
                    margin: "0 0 24px 0",
                    fontSize: 28,
                    fontWeight: 700,
                    textAlign: "center"
                }}>
                    {t('experiments', 'attributeFraming', 'screens', 'task', 'title')}
                </h2>

                <div style={{
                    fontSize: 17,
                    lineHeight: 1.7,
                    color: colors.textMedium,
                    textAlign: "justify",
                    textJustify: "inter-word"
                }}>
                    <p style={{ marginTop: 0, marginBottom: 20 }}>
                        {t('experiments', 'attributeFraming', 'screens', 'task', 'instructions')}
                    </p>
                </div>
            </div>

            {/* Question header */}
            <h3 style={{
                color: colors.brillantBlue,
                textAlign: "center",
                marginBottom: 20,
                fontSize: 24,
                fontWeight: 600
            }}>
                {t('experiments', 'attributeFraming', 'screens', 'task', 'question')}
            </h3>

            {/* Cup options */}
            <CupOptions
                selected={selected}
                setSelected={setSelected}
            />

            {/* Reason field (shown always) */}
            <div style={{ marginTop: 38, maxWidth: 670, marginLeft: "auto", marginRight: "auto" }}>
                <label style={{
                    display: "block",
                    fontWeight: 600,
                    color: colors.textDark,
                    marginBottom: 8
                }}>
                    {t('experiments', 'attributeFraming', 'screens', 'task', 'reasonLabel') ||
                        (language === "de"
                            ? "Bitte begründen Sie Ihre Auswahl:"
                            : "Please explain your choice:")}{" "}
                    <span style={{ fontWeight: 500, color: colors.textMedium }}>
                        {t('experiments', 'attributeFraming', 'screens', 'task', 'reasonRequired') ||
                            (language === "de" ? "(erforderlich)" : "(required)")}
                    </span>
                </label>
                <textarea
                    aria-label={
                        t('experiments', 'attributeFraming', 'screens', 'task', 'reasonLabel') ||
                        (language === "de"
                            ? "Begründung für die Auswahl"
                            : "Reason for choice")
                    }
                    placeholder={
                        t('experiments', 'attributeFraming', 'screens', 'task', 'reasonPlaceholder') ||
                        (language === "de"
                            ? "Bitte gib eine kurze Begründung an."
                            : "Please enter a brief explanation.")
                    }
                    value={reason}
                    onChange={e => setReason(e.target.value)}
                    onBlur={() => setReasonTouched(true)}
                    rows={4}
                    style={{
                        width: "100%",
                        maxWidth: 640,
                        minHeight: 96,
                        padding: 12,
                        fontSize: 15,
                        borderRadius: 8,
                        border: "1px solid #e1e6ea",
                        resize: "vertical",
                        boxShadow: "0 2px 6px rgba(0,0,0,0.03)"
                    }}
                />
                {reasonError && <div style={{ color: "red", marginTop: 8 }}>{reasonError}</div>}
            </div>

            {/* Selection confirmation */}
            {selected !== null && (
                <div style={{
                    textAlign: "center",
                    marginTop: 28,
                    padding: "12px 20px",
                    fontSize: 18,
                    fontWeight: 500,
                    color: colors.brillantBlue,
                    backgroundColor: colors.lichtBlauLighter,
                    borderRadius: 8,
                    display: "inline-block",
                    marginLeft: "auto",
                    marginRight: "auto"
                }}>
                    <Icon icon="tick-circle" style={{ marginRight: 8 }} />
                    {selected === 0
                        ? t('experiments', 'attributeFraming', 'screens', 'task', 'selection', 'recycled')
                        : t('experiments', 'attributeFraming', 'screens', 'task', 'selection', 'new')
                    }
                </div>
            )}

            {/* Extra explanation below reason */}
            <div style={{ fontSize: 14, color: colors.textMedium, textAlign: "center", marginTop: 22 }}>
                {language === "de"
                    ? "Deine Entscheidung ist erst mit einer kurzen Begründung gültig."
                    : "Your decision is only valid with a brief explanation."}
            </div>
        </div>
    );
}

export const AttributeScreen1Config: ScreenConfig = {
    component: AttributeScreen1,
    defaultResult: AttributeScreenInitialResult,
    canContinueDefault: false
};